(() => {
  "use strict";

  if (window.misyCartRecommendationsReady) return;
  window.misyCartRecommendationsReady = true;

  const selector = "[data-misy-recommendations]";
  const routeRoot = window.Shopify?.routes?.root || "/";
  const observed = new WeakSet();
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);
        void populate(entry.target);
      }
    }
  }, { rootMargin: "200px" });
  let scanQueued = false;

  function productPath(link) {
    try {
      return new URL(link, window.location.origin).pathname;
    } catch (_) {
      return "";
    }
  }

  async function fetchRecommendations(productId, intent) {
    const url = new URL(`${routeRoot}recommendations/products`, window.location.origin);
    url.searchParams.set("product_id", productId);
    url.searchParams.set("limit", "10");
    url.searchParams.set("intent", intent);
    url.searchParams.set("section_id", "misy-cart-recommendations");
    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) return [];
    const html = new DOMParser().parseFromString(await response.text(), "text/html");
    return Array.from(html.querySelectorAll("[data-misy-recommendation-results] > [data-misy-recommended-product-id]"));
  }

  async function populate(list) {
    if (list.dataset.misyRecommendationsLoading || list.dataset.misyRecommendationsLoaded) return;
    const sourceIds = [...new Set((list.dataset.productIds || "").split(",").filter(Boolean))].slice(0, 2);
    if (!sourceIds.length) return;
    list.dataset.misyRecommendationsLoading = "true";

    try {
      const cartIds = new Set((list.dataset.cartProductIds || "").split(","));
      const gapPaths = new Set(Array.from(document.querySelectorAll(".misy-cart-gap:not(.misy-cart-suggestions) a[href*='/products/']"), (link) => productPath(link.href)));
      const chosen = [];
      const chosenIds = new Set();
      const chosenPaths = new Set();

      for (const productId of sourceIds) {
        const [complementary, related] = await Promise.all([
          fetchRecommendations(productId, "complementary"),
          fetchRecommendations(productId, "related")
        ]);

        for (const card of [...complementary, ...related]) {
          const id = card.dataset.misyRecommendedProductId;
          const path = productPath(card.querySelector("a[href*='/products/']")?.href || "");
          if (!id || !path || cartIds.has(id) || chosenIds.has(id) || chosenPaths.has(path) || gapPaths.has(path)) continue;
          chosen.push(card);
          chosenIds.add(id);
          chosenPaths.add(path);
          if (chosen.length === 3) break;
        }
        if (chosen.length === 3) break;
      }

      if (!list.isConnected || !chosen.length) return;
      const fallbackCards = Array.from(list.querySelectorAll("[data-misy-fallback-card]"));
      const keptFallback = fallbackCards.filter((card) => {
        const path = productPath(card.querySelector("a[href*='/products/']")?.href || "");
        return path && !chosenPaths.has(path) && !gapPaths.has(path);
      }).slice(0, 3 - chosen.length);
      fallbackCards.forEach((card) => card.remove());
      list.prepend(...chosen, ...keptFallback);
      list.closest(".misy-cart-suggestions")?.removeAttribute("hidden");
      list.dataset.misyRecommendationsSource = "shopify";
    } catch (_) {
      return;
    } finally {
      if (list.isConnected) {
        delete list.dataset.misyRecommendationsLoading;
        list.dataset.misyRecommendationsLoaded = "true";
      }
    }
  }

  function scan() {
    document.querySelectorAll(selector).forEach((list) => {
      if (!observed.has(list)) {
        observed.add(list);
        if (list.closest(".misy-cart-suggestions")?.hidden) void populate(list);
        else observer.observe(list);
      }
    });
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => {
      scanQueued = false;
      scan();
    });
  }

  function initialize() {
    scheduleScan();
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE && (node.matches(selector) || node.querySelector(selector))) {
            scheduleScan();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener("cart-drawer:refreshed", scheduleScan);
  document.addEventListener("shopify:section:load", scheduleScan);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialize, { once: true });
  else initialize();
})();
